import React, { useEffect, useState, useCallback, useRef } from "react";
import { ESTADOS_FORMACION } from "../constants/estadosFormacion";
import { listarFormacionesPorEstudiante } from "../../../../services/formacionesService";
import { listarEstudiantes } from "../../../../services/estudiantesService";
import { showToast } from "../../../../utils/showToast";

const ModalWrapper = ({ open, title, children, onClose }) => {
  if (!open) return null;
  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

const formatDate = (value) => (value ? String(value).split("T")[0] : "");

export default function FormacionFormModal({
  modal,
  formNuevo,
  setFormNuevo,
  formEditar,
  setFormEditar,
  saving,
  deleting,
  formacionEditando,
  estudianteFijo = null,
  cerrarModal,
  handleCrear,
  handleActualizar,
  handleEliminar,
  matriculasOptions = [],
  cursosOptions = [],
  loadingOptions = false,
  reloadOptions,
}) {
  const [baseOptionsNuevo, setBaseOptionsNuevo] = useState([]);
  const [baseOptionsEditar, setBaseOptionsEditar] = useState([]);
  const [loadingBasesNuevo, setLoadingBasesNuevo] = useState(false);
  const [loadingBasesEditar, setLoadingBasesEditar] = useState(false);
  const [searchEstudianteNuevo, setSearchEstudianteNuevo] = useState("");
  const [searchEstudianteEditar, setSearchEstudianteEditar] = useState("");
  const [resultadosEstudianteNuevo, setResultadosEstudianteNuevo] = useState([]);
  const [buscandoEstudianteNuevo, setBuscandoEstudianteNuevo] = useState(false);
  const [estudianteSeleccionadoNuevo, setEstudianteSeleccionadoNuevo] = useState(null);
  const didInitEditSearch = useRef(false);

  const ensureOptionExists = (options, currentId, formacionData = null) => {
    if (!currentId) return options;
    const exists = options.some((opt) => String(opt.value) === String(currentId));
    if (exists) return options;

    const estudiante =
      formacionData?.estudiante || formacionData?.estudianteNombre || formacionData?.nombre || formacionData?.label || "";
    const codigoFallback = formacionData?.codigoMatricula || formacionData?.codigo_matricula || `#${currentId}`;
    const label = formacionData
      ? `Codigo ${codigoFallback} - ${estudiante || "Estudiante desconocido"}`
      : `Codigo ${codigoFallback} (no listado)`;

    return [
      ...options,
      { value: String(currentId), label, estudianteId: formacionData?.estudianteId, estudianteNombre: estudiante },
    ];
  };

  const cursoInfo = useCallback(
    (id) => {
      if (id === undefined || id === null || id === "") return undefined;
      const target = Number(id);
      return cursosOptions.find((c) => Number(c.value) === target);
    },
    [cursosOptions]
  );

  const loadBases = useCallback(
    async ({ estudiante_id, curso_id, nivel_id }, setBaseOptions, setLoading) => {
      const nivelTarget = Number(nivel_id || 0);
      if (!estudiante_id || !curso_id || !nivelTarget) {
        setBaseOptions([]);
        return;
      }
      setLoading(true);
      try {
        const formaciones = await listarFormacionesPorEstudiante(estudiante_id);
        const acreditadas = (formaciones || []).filter(
          (f) => f.estado === "acreditado" && Number(f.nivel_id ?? f.nivelId) === nivelTarget
        );
        const mapped = acreditadas.map((f) => ({
          value: f.id,
          label: `Formacion #${f.id} - Estado: ${f.estado}`,
        }));
        setBaseOptions(mapped);
        if (!mapped.length) {
          showToast({
            type: "warning",
            message: "El estudiante no tiene una formacion acreditada de este nivel.",
          });
        }
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo cargar la formacion base." });
        setBaseOptions([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const needsBaseNuevo = formNuevo.matricula_id === "" && Number(formNuevo.es_reentrenamiento) === 1;
  const needsBaseEditar = formEditar.matricula_id === "" && Number(formEditar.es_reentrenamiento) === 1;

  // Busqueda de estudiante en servidor (no depende de una lista precargada,
  // que antes se truncaba a los 50 estudiantes mas recientes y ocultaba a
  // estudiantes antiguos del selector).
  useEffect(() => {
    if (modal !== "crear" || formNuevo.matricula_id || formNuevo.estudiante_id) {
      setResultadosEstudianteNuevo([]);
      return undefined;
    }
    const term = searchEstudianteNuevo.trim();
    const esNumerico = /^\d+$/.test(term);
    const minChars = esNumerico ? 5 : 3;
    if (term.length < minChars) {
      setResultadosEstudianteNuevo([]);
      return undefined;
    }
    let cancelado = false;
    setBuscandoEstudianteNuevo(true);
    const timer = setTimeout(async () => {
      try {
        const data = await listarEstudiantes({ search: term, porPagina: 20 });
        const arr = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        if (cancelado) return;
        setResultadosEstudianteNuevo(
          arr.map((e) => {
            const nombre = `${e.nombres || ""} ${e.apellidos || ""}`.trim();
            return {
              value: e.id,
              label: nombre || `Estudiante #${e.id}`,
              nombre,
              documento: e.documento_identidad || e.documento || "",
            };
          })
        );
      } catch (err) {
        if (!cancelado) {
          console.error(err);
          showToast({ type: "danger", message: err.message || "No se pudo buscar el estudiante." });
          setResultadosEstudianteNuevo([]);
        }
      } finally {
        if (!cancelado) setBuscandoEstudianteNuevo(false);
      }
    }, 350);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [modal, searchEstudianteNuevo, formNuevo.matricula_id, formNuevo.estudiante_id]);

  useEffect(() => {
    if (modal === "crear" && needsBaseNuevo) {
      loadBases(formNuevo, setBaseOptionsNuevo, setLoadingBasesNuevo);
    } else if (modal === "crear") {
      setBaseOptionsNuevo([]);
    }
  }, [modal, needsBaseNuevo, formNuevo, loadBases, setBaseOptionsNuevo, setLoadingBasesNuevo]);

  useEffect(() => {
    if (modal === "editar" && needsBaseEditar) {
      loadBases(formEditar, setBaseOptionsEditar, setLoadingBasesEditar);
    } else if (modal === "editar") {
      setBaseOptionsEditar([]);
    }
  }, [modal, needsBaseEditar, formEditar, loadBases, setBaseOptionsEditar, setLoadingBasesEditar]);

  useEffect(() => {
    if (!modal) {
      setSearchEstudianteNuevo("");
      setSearchEstudianteEditar("");
      setResultadosEstudianteNuevo([]);
      setEstudianteSeleccionadoNuevo(null);
    }
  }, [modal]);

  useEffect(() => {
    if (modal !== "editar") {
      didInitEditSearch.current = false;
      return;
    }
    if (didInitEditSearch.current) return;
    if (formEditar.matricula_id) {
      didInitEditSearch.current = true;
      return;
    }
    const label = formEditar.estudiante_id
      ? formacionEditando?.estudiante || formacionEditando?.estudianteNombre || ""
      : "";
    if (label) {
      setSearchEstudianteEditar(label);
      didInitEditSearch.current = true;
    }
  }, [modal, formEditar.matricula_id, formEditar.estudiante_id, formacionEditando]);

  // Mantener flag de reentrenamiento alineado al curso seleccionado
  useEffect(() => {
    const info = cursoInfo(formNuevo.curso_id);
    if (info) {
      setFormNuevo((p) => ({
        ...p,
        es_reentrenamiento: info.es_reentrenamiento ? 1 : 0,
        nivel_id: info.nivel_id,
      }));
    }
  }, [formNuevo.curso_id, cursoInfo, setFormNuevo]);

  useEffect(() => {
    const info = cursoInfo(formEditar.curso_id);
    if (info) {
      setFormEditar((p) => ({
        ...p,
        es_reentrenamiento: info.es_reentrenamiento ? 1 : 0,
        nivel_id: info.nivel_id,
      }));
    }
  }, [formEditar.curso_id, cursoInfo, setFormEditar]);

  const renderOptions = (options, placeholder) => {
    if (loadingOptions) return <option value="">{`Cargando ${placeholder}...`}</option>;
    if (!options.length) return <option value="">No hay {placeholder} disponibles</option>;
    return [
      <option key="placeholder" value="">
        {`Selecciona ${placeholder}`}
      </option>,
      ...options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      )),
    ];
  };

  const renderForm = (form, setForm, onSubmit, isEdit = false, formacionData = null) => {
    // Solo aplica al crear: si "Nueva formación" se abrió desde el perfil de un
    // estudiante, ese estudiante ya viene fijo y se bloquea el campo (ver abajo).
    const fijo = !isEdit ? estudianteFijo : null;
    const optionsWithCurrent = ensureOptionExists(matriculasOptions, form.matricula_id, formacionData);
    const optionsAvailable = optionsWithCurrent.length > 0;
    const selectedMatricula = optionsWithCurrent.find((opt) => String(opt.value) === String(form.matricula_id));
    const estudianteNombre =
      selectedMatricula?.nombre ||
      selectedMatricula?.estudianteNombre ||
      selectedMatricula?.label ||
      formacionData?.estudiante ||
      formacionData?.estudianteNombre ||
      formacionData?.nombre ||
      formacionData?.label ||
      form.estudiante_nombre;
    const estudianteId = selectedMatricula?.estudianteId || formacionData?.estudianteId || form.estudiante_id;
    const course = cursoInfo(form.curso_id);
    const baseOptions = isEdit ? baseOptionsEditar : baseOptionsNuevo;
    const loadingBases = isEdit ? loadingBasesEditar : loadingBasesNuevo;
    const isReentrenamiento = Number(form.es_reentrenamiento) === 1 || Number(course?.es_reentrenamiento) === 1;
    const estudianteSearchTerm = isEdit ? searchEstudianteEditar : searchEstudianteNuevo;
    const setEstudianteSearchTerm = isEdit ? setSearchEstudianteEditar : setSearchEstudianteNuevo;
    const estudiantesFiltrados = isEdit ? [] : resultadosEstudianteNuevo;
    const selectedEstudiante = isEdit ? undefined : estudianteSeleccionadoNuevo;
    const showResultadosEstudiante =
      !isEdit && Boolean(estudianteSearchTerm.trim()) && estudiantesFiltrados.length > 0 && !form.estudiante_id;

    const handleMatriculaChange = (value, selectedOption = null) => {
      const selected = selectedOption || optionsWithCurrent.find((opt) => String(opt.value) === String(value));
      setForm((p) => ({
        ...p,
        matricula_id: value,
        estudiante_id: selected?.estudianteId ? String(selected.estudianteId) : p.estudiante_id,
        curso_id: value ? "" : p.curso_id,
        nivel_id: value ? "" : p.nivel_id,
        es_reentrenamiento: value ? 0 : p.es_reentrenamiento,
        formacion_origen_id: "",
        centro_formacion: value ? "" : p.centro_formacion,
      }));
      if (!value) {
        setEstudianteSearchTerm("");
        return;
      }
      if (selected?.documento) {
        setEstudianteSearchTerm(selected.documento);
      } else if (selected?.nombre || selected?.label) {
        setEstudianteSearchTerm(selected.nombre || selected.label);
      } else {
        setEstudianteSearchTerm(`Codigo ${value}`);
      }
    };

    const handleCursoChange = (value) => {
      const info = cursoInfo(value);
      setForm((p) => ({
        ...p,
        curso_id: value,
        nivel_id: info?.nivel_id ? Number(info.nivel_id) : "",
        es_reentrenamiento: info?.es_reentrenamiento ? 1 : 0,
        formacion_origen_id: "",
      }));
    };

    const handleSelectEstudiante = (value, selected = null) => {
      if (!value) {
        setForm((p) => ({ ...p, estudiante_id: "" }));
        setEstudianteSearchTerm("");
        if (!isEdit) setEstudianteSeleccionadoNuevo(null);
        return;
      }
      const target = selected || resultadosEstudianteNuevo.find((opt) => String(opt.value) === String(value));
      setForm((p) => ({
        ...p,
        estudiante_id: String(value),
        matricula_id: "",
        formacion_origen_id: "",
      }));
      if (!isEdit) setEstudianteSeleccionadoNuevo(target || null);
      if (target?.documento) {
        setEstudianteSearchTerm(target.documento);
      } else if (target?.nombre || target?.label) {
        setEstudianteSearchTerm(target.nombre || target.label);
      }
    };

    const matriculasDelEstudiante = (() => {
      if (!form.estudiante_id) return [];
      return optionsWithCurrent.filter(
        (opt) => String(opt.estudianteId ?? opt.estudiante_id ?? "") === String(form.estudiante_id)
      );
    })();

    const matriculasSelectOptions = (() => {
      const base = matriculasDelEstudiante;
      if (!form.matricula_id) return base;
      const exists = base.some((opt) => String(opt.value) === String(form.matricula_id));
      if (exists) return base;
      return selectedMatricula ? [...base, selectedMatricula] : base;
    })();

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div className="modal-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Codigo matricula (opcional)</label>
              <div className="input-group">
                <select
                  className="form-select"
                  value={form.matricula_id}
                  onChange={(e) => handleMatriculaChange(e.target.value)}
                  disabled={loadingOptions || isEdit}
                >
                  <option value="">Crear sin matricula</option>
                  {loadingOptions && !optionsAvailable && (
                    <option value="" disabled>
                      Cargando codigos...
                    </option>
                  )}
                  {!loadingOptions && !form.estudiante_id && !form.matricula_id && (
                    <option value="" disabled>
                      Selecciona un estudiante para ver sus codigos
                    </option>
                  )}
                  {!loadingOptions && form.estudiante_id && matriculasSelectOptions.length === 0 && (
                    <option value="" disabled>
                      Sin codigos asociados
                    </option>
                  )}
                  {matriculasSelectOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {`Codigo ${opt.codigoMatricula || `#${opt.value}`}`}
                    </option>
                  ))}
                </select>
                {form.matricula_id && !isEdit && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    title="Limpiar seleccion"
                    onClick={() => handleMatriculaChange("")}
                  >
                    <i className="bi bi-x-circle" />
                  </button>
                )}
                {!isEdit && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    title="Refrescar"
                    disabled={loadingOptions}
                    onClick={reloadOptions}
                  >
                    <i className={`bi bi-arrow-repeat ${loadingOptions ? "spin" : ""}`} />
                  </button>
                )}
              </div>
              {!isEdit && (
                <small className="text-muted d-block mt-1">
                  Si no ves opciones, recarga o escribe el estudiante en el buscador.
                </small>
              )}
              {isEdit && (
                <small className="text-muted d-block mt-1">
                  Para mantener integridad, la matricula no se edita desde aqui.
                </small>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">Estudiante</label>
              {form.matricula_id ? (
                <input
                  type="text"
                  className="form-control"
                  value={estudianteNombre || (estudianteId ? `ID #${estudianteId}` : "")}
                  disabled
                  readOnly
                  placeholder="Selecciona un codigo"
                />
              ) : isEdit ? (
                <input
                  type="text"
                  className="form-control"
                  value={
                    estudianteSearchTerm ||
                    selectedEstudiante?.nombre ||
                    selectedEstudiante?.label ||
                    (form.estudiante_id ? `ID #${form.estudiante_id}` : "")
                  }
                  disabled
                  readOnly
                  placeholder="Selecciona un estudiante"
                />
              ) : fijo ? (
                <>
                  <input type="text" className="form-control" value={fijo.nombre} disabled readOnly />
                  <small className="text-muted d-block mt-1">
                    <i className="bi bi-lock-fill me-1" />
                    Formación para este estudiante — se creó desde su perfil.
                  </small>
                </>
              ) : (
                <>
                  <div className="input-group mb-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar por nombre o documento"
                      value={estudianteSearchTerm}
                      onChange={(e) => setEstudianteSearchTerm(e.target.value)}
                      disabled={loadingOptions}
                    />
                    {estudianteSearchTerm && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => handleSelectEstudiante("")}
                      >
                        Limpiar
                      </button>
                    )}
                  </div>
                  {selectedEstudiante && (
                    <small className="text-muted d-block mt-1">
                      Estudiante seleccionado: {selectedEstudiante.nombre || selectedEstudiante.label}
                      {selectedEstudiante.documento ? ` | ${selectedEstudiante.documento}` : ""}
                    </small>
                  )}
                  {!isEdit && buscandoEstudianteNuevo && (
                    <small className="text-info d-block mt-1">
                      <i className="bi bi-search me-1" />
                      Buscando estudiante...
                    </small>
                  )}
                  {!isEdit &&
                    !buscandoEstudianteNuevo &&
                    !form.estudiante_id &&
                    estudianteSearchTerm.trim().length >= (/^\d+$/.test(estudianteSearchTerm.trim()) ? 5 : 3) &&
                    estudiantesFiltrados.length === 0 && (
                      <small className="text-muted d-block mt-1">Sin resultados para esa busqueda.</small>
                    )}
                  {showResultadosEstudiante && (
                    <div className="mt-2">
                      <small className="text-muted d-block mb-1">Resultados de la busqueda</small>
                      <div className="list-group small formaciones-estudiantes-results">
                        {estudiantesFiltrados.map((opt) => (
                          <button
                            type="button"
                            key={opt.value}
                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-start"
                            onClick={() => handleSelectEstudiante(String(opt.value), opt)}
                          >
                            <div className="me-2">
                              <div className="fw-semibold">{opt.nombre || opt.label}</div>
                              <div className="text-muted">
                                {opt.documento ? `Doc: ${opt.documento}` : "Sin documento"}
                              </div>
                            </div>
                            <small className="text-muted">#{opt.value}</small>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {!form.matricula_id && (
              <>
                <div className="col-md-8">
                  <label className="form-label">Curso</label>
                  <select
                    className="form-select"
                    value={form.curso_id}
                    onChange={(e) => handleCursoChange(e.target.value)}
                  >
                    {renderOptions(cursosOptions, "un curso")}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Nivel</label>
                  <input
                    type="text"
                    className="form-control"
                    value={
                      course?.nivel_id
                        ? `Nivel ${course.nivel_id}`
                        : form.nivel_id
                          ? `Nivel ${form.nivel_id}`
                          : ""
                    }
                    readOnly
                    disabled
                  />
                </div>
                <div className="col-md-12">
                  <label className="form-label">Centro donde realizo el curso</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.centro_formacion || ""}
                    onChange={(e) => setForm((p) => ({ ...p, centro_formacion: e.target.value }))}
                    placeholder="Ej: ITV"
                    maxLength={150}
                  />
                  <small className="text-muted d-block mt-1">
                    Si la formacion fue externa, escribe el nombre del centro real.
                  </small>
                </div>
                {isReentrenamiento && (
                  <div className="col-md-12">
                    <label className="form-label">Formación base acreditada</label>
                    <select
                      className="form-select"
                      value={form.formacion_origen_id}
                      onChange={(e) => setForm((p) => ({ ...p, formacion_origen_id: e.target.value }))}
                      disabled={loadingBases}
                    >
                      {loadingBases ? (
                        <option value="">Cargando bases...</option>
                      ) : baseOptions.length ? (
                        [
                          <option key="placeholder" value="">
                            Selecciona formación base
                          </option>,
                          ...baseOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          )),
                        ]
                      ) : (
                        <option value="">Sin formaciones acreditadas del nivel</option>
                      )}
                    </select>
                    {!baseOptions.length && (
                      <small className="text-warning d-block mt-1">
                        Debes registrar o seleccionar una formación acreditada de este nivel antes de continuar.
                      </small>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="col-md-4">
              <label className="form-label">Fecha inicio</label>
              <input
                type="date"
                className="form-control"
                value={formatDate(form.fecha_inicio)}
                onChange={(e) => setForm((p) => ({ ...p, fecha_inicio: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Fecha fin</label>
              <input
                type="date"
                className="form-control"
                value={formatDate(form.fecha_fin)}
                onChange={(e) => setForm((p) => ({ ...p, fecha_fin: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Fecha aprobación</label>
              <input
                type="date"
                className="form-control"
                value={formatDate(form.fecha_aprobacion)}
                onChange={(e) => setForm((p) => ({ ...p, fecha_aprobacion: e.target.value }))}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Estado</label>
              <select
                className="form-select"
                value={form.estado}
                onChange={(e) => setForm((p) => ({ ...p, estado: e.target.value }))}
              >
                {ESTADOS_FORMACION.map((estado) => (
                  <option key={estado.value} value={estado.value}>
                    {estado.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 d-flex align-items-center gap-2">
              <input
                id={`activo-${isEdit ? "edit" : "new"}`}
                type="checkbox"
                className="form-check-input"
                checked={Boolean(form.activo)}
                onChange={(e) => setForm((p) => ({ ...p, activo: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor={`activo-${isEdit ? "edit" : "new"}`}>
                Formación activa
              </label>
            </div>
            {reloadOptions && (
              <div className="col-12">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={reloadOptions}
                  disabled={loadingOptions}
                >
                  <i className="bi bi-arrow-repeat me-2" />
                  {loadingOptions ? "Actualizando listas..." : "Actualizar listas"}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer border-0">
          <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" className="btn management-btn-primary" disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    );
  };

  return (
    <>
      <ModalWrapper
        open={modal === "crear"}
        title={estudianteFijo ? `Nueva formación — ${estudianteFijo.nombre}` : "Nueva formación"}
        onClose={cerrarModal}
      >
        {renderForm(formNuevo, setFormNuevo, handleCrear, false)}
      </ModalWrapper>

      <ModalWrapper open={modal === "editar"} title="Editar formación" onClose={cerrarModal}>
        {renderForm(formEditar, setFormEditar, handleActualizar, true, formacionEditando)}
      </ModalWrapper>

      <ModalWrapper open={modal === "eliminar"} title="Eliminar formación" onClose={cerrarModal}>
        <div className="modal-body">
          <p className="mb-1">Esta acción desactivará la formación seleccionada.</p>
          <small className="text-muted">
            Codigo matricula {formacionEditando?.codigoMatricula || (formacionEditando?.matriculaId ? `#${formacionEditando?.matriculaId}` : "-")} · Estudiante #{formacionEditando?.estudiante?.estudiante_nombre}
          </small>
        </div>
        <div className="modal-footer border-0">
          <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal} disabled={deleting}>
            Cancelar
          </button>
          <button type="button" className="btn btn-danger" onClick={handleEliminar} disabled={deleting}>
            {deleting ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </ModalWrapper>
    </>
  );
}


